// server/browser/src/lib/paddle.ts
//
// Paddle.js overlay checkout. The cloud creates the transaction
// server-side (so price + neos_sub are authoritative) and returns its id;
// here we just load Paddle.js, initialise it with the public client token,
// and open the overlay against that transaction.

interface PaddleGlobal {
  Environment: { set(env: string): void };
  Initialize(opts: { token: string }): void;
  Checkout: {
    open(opts: {
      transactionId: string;
      settings?: {
        successUrl?: string;
        /** "light" | "dark" — overlay's own background + form text. The
         *  primary button colour and logo come from Paddle Dashboard →
         *  Branding (set there once, applies everywhere — overlay, portal,
         *  receipts). */
        theme?: "light" | "dark";
        displayMode?: "overlay" | "inline";
      };
    }): void;
  };
}

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

const PADDLE_JS = "https://cdn.paddle.com/paddle/v2/paddle.js";

let loading: Promise<PaddleGlobal> | null = null;

function loadScript(): Promise<PaddleGlobal> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  if (loading) return loading;
  loading = new Promise<PaddleGlobal>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = PADDLE_JS;
    s.async = true;
    s.onload = () => {
      if (window.Paddle) resolve(window.Paddle);
      else reject(new Error("Paddle.js loaded but window.Paddle is missing"));
    };
    s.onerror = () => reject(new Error("Failed to load Paddle.js"));
    document.head.appendChild(s);
  });
  // A failed load must not poison the cache: drop the rejected promise
  // so a later checkout attempt re-tries the script load instead of
  // replaying the original rejection forever.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/** Open the Paddle overlay for a backend-created transaction. */
export async function openCheckout(
  clientToken: string,
  environment: string,
  transactionId: string,
  successUrl: string,
): Promise<void> {
  const Paddle = await loadScript();
  if (environment === "sandbox") Paddle.Environment.set("sandbox");
  Paddle.Initialize({ token: clientToken });
  // Dark theme matches the rest of optioway.com (slate-900 background,
  // indigo accents). The deeper brand colour (primary button, logo) is set
  // on Paddle's side in Dashboard → Branding so it stays consistent across
  // overlay, customer portal, and emailed receipts.
  Paddle.Checkout.open({
    transactionId,
    settings: { successUrl, theme: "dark", displayMode: "overlay" },
  });
}
